import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpService } from './http.service';

export interface TtsVoiceSettings {
  stability?: number;
  similarityBoost?: number;
  style?: number;
  useSpeakerBoost?: boolean;
  speed?: number;
}

// What the audio was synthesized with, every default resolved, under the names
// create-speech takes. Rows stored before content-api resolved them may be partial.
export interface TtsSettings {
  voiceId?: string;
  modelId?: string;
  languageCode?: string;
  outputFormat?: string;
  applyTextNormalization?: 'auto' | 'on' | 'off';
  applyLanguageTextNormalization?: boolean;
  pronunciationDictionaryLocators?: unknown[];
  voiceSettings?: TtsVoiceSettings;
}

export interface TtsStatusRow {
  fieldPath: string;
  fieldPathSlug: string;
  status: 'pending' | 'ready' | 'failed';
  url: string;
  updatedAt: string;
  text?: string;
  settings?: TtsSettings;
}

// content-api wraps every payload in `{ data }` (Response.success).
function unwrap(response: any): TtsStatusRow[] {
  if (Array.isArray(response)) {
    return response;
  }
  return response?.data ?? [];
}

@Injectable({
  providedIn: 'root',
})
export class TextToSpeechService {
  private static readonly REQUESTER = 'cms';
  private static readonly ENTITY_TYPE = 'curriculum-session';

  constructor(private http: HttpService) {}

  status(entityId: string, fieldPaths?: string[]): Observable<TtsStatusRow[]> {
    const params: any = {
      requester: TextToSpeechService.REQUESTER,
      entityType: TextToSpeechService.ENTITY_TYPE,
      entityId,
    };

    if (fieldPaths && fieldPaths.length > 0) {
      params.fieldPath = fieldPaths;
    }

    return this.http.get('text-to-speech', params).pipe(map(unwrap));
  }

  regenerate(entityId: string, fieldPath: string, text: string, force = false): Observable<TtsStatusRow[]> {
    const payload: any = {
      requester: TextToSpeechService.REQUESTER,
      entityType: TextToSpeechService.ENTITY_TYPE,
      entityId,
      items: [{ fieldPath, text }],
    };

    // Top-level, and safe only because this list is one item long.
    if (force) {
      payload.force = true;
    }

    return this.http.post('text-to-speech/create-speech', payload).pipe(map(unwrap));
  }
}
